import { useContext, useEffect, useState, } from "react";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import ListPage from "../../../shared/components/list-page/list-page";
import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { roleWrite, roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, deleteMultiple, IListPageData } from "../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import { datamodelEventListUrl } from "../../constants/backend-constants";
import "./event-list.scss";

const columns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    model: "event",
    dataType: {
      type: "text",
    }
  },
  {
    key: "total_rows",
    displayName: "Total rows",
    dataType: {
      type: "text",
    },
    nonSortable: true,
  },
  {
    key: "last_imported_date",
    displayName: "Last imported date",
    dataType: {
      type: "dateTime",
    },
    nonSortable: true,
  },
  {
    key: "archived",
    displayName: "Archived",
    model: "event",
    dataType: {
      type: "text",
    },
  },
];

const EventsList = () => {
  const [data, setData] = useState<IListPageData | undefined>(undefined);
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=name asc");
  const [currentOffset, setCurrentOffset] = useState(1);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [idsToBeDeleted, setIdsToBeDeleted] = useState<string[]>([]);
  const authCtx = useContext(AuthContext);

  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttp(getAllData, true, authCtx.token);
  const { sendRequest: getSortByPagination, status: getSortByPaginationStatus, data: getSortByPaginationData, error: getSortByPaginationError } = useHttp(getAllData, false, authCtx.token);
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token, "archived");

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  const onTableScrollAction = (offset: number) => {
    if (data && data.rows.length >= 50) {
      setCurrentOffset(offset);
      if (!getSortByPaginationData) {
        getSortByPagination("datamodel/events", `?order_by=name asc&limit=50&offset=${offset * 50}`);
      } else if (getSortByPaginationData.rows.length !== 0) {
        getSortByPagination("datamodel/events", `?order_by=name asc&limit=50&offset=${offset * 50}`);
      }
    }
  };

  useEffect(() => {
    if (getSortByPaginationStatus === "completed" && !getSortByPaginationError) {
      data && setData({ rows: [...data.rows, ...getSortByPaginationData.rows] });
    }
  }, [getSortByPaginationStatus]);

  useEffect(() => {
    getSortBy("datamodel/events", "?order_by=name asc&limit=50&offset=0");
    authCtx.setOnboardingPlatformToggleHandler();
  }, [getSortBy]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData(getSortByData);
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      authCtx.setOnboardingPlatformToggleHandler();
      getSortBy("datamodel/events", `${sortedBy}&limit=${currentOffset * 50}&offset=0`);
      setIdsToBeDeleted([]);
      setConfirmationPopup(false);
    }
  }, [allStatusDelete]);

  const handleOnDelete = (ids: string[]) => {
    const archivedItems = data?.rows.filter((row) => ids.includes(row.id) && row.archived);
    if (archivedItems?.length) {
      setConfirmationPopup(true);
      setIdsToBeDeleted(ids);
    } else {
      deleteMultipleRequest(`datamodel/events${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids });
    }
  };

  const handleOnDeleteConfirmSubmit = () => {
    deleteMultipleRequest(`datamodel/events${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids: idsToBeDeleted });
  };

  const sortBy = (sortingQuery: string) => {
    getSortBy("datamodel/events", `${sortingQuery}&limit=${currentOffset * 50}&offset=0`);
    setSortedBy(sortingQuery);
  };

  return (
    <div className="eventList">
      <ConfirmationPopup
        confirmButtonText={"Delete"}
        text={"Some selected events are archived. Are you sure you want to delete them?"}
        show={confirmationPopup}
        handlePopupClosed={() => setConfirmationPopup(false)}
        handleSaveClick={handleOnDeleteConfirmSubmit}
      />
      {validateLoadingStatusNew([getSortByStatus, allStatusDelete, getSortByPaginationStatus]) && <Spinner />}
      <ListPage
        title={"Event types"}
        defaultSortCol={"name"}
        sortBy={sortBy}
        onTableScrollAction={onTableScrollAction}
        columns={columns}
        unblockInput={unblockInput}
        dataUrl={datamodelEventListUrl}
        pageMode={"nonEditable"}
        handleOnDelete={handleOnDelete}
        fetchedData={data}
        allStatus={getSortByStatus}
        allStatusDelete={allStatusDelete}
        tooltipTxt={"You can create sets of Events which is required for the model generation"}
      />
    </div>
  );
};

export default EventsList;